import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ArrowRight, CheckCircle, ChevronRight, Sparkles,
  Building2, UserCog, Plus, AlertTriangle, BarChart3, Users, Calendar
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';

const OB_KEY = 'd3_onboarding';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'executive', label: 'Executive / C-Suite' },
  { value: 'manager', label: 'Manager / Team Lead' },
  { value: 'operations', label: 'Operations' },
  { value: 'risk_compliance', label: 'Risk & Compliance' },
  { value: 'other', label: 'Other' },
];

const SAMPLE_TASKS = [
  'Review Q2 project milestones',
  'Follow up on vendor contract renewal',
  'Finalize budget allocation for new initiative',
  'Schedule team sync on delivery timeline',
];

const SAMPLE_RISKS = [
  'Key team member availability during Q2 crunch',
  'Vendor delivery delay impacting project timeline',
  'Budget overrun risk on infrastructure upgrade',
  'Compliance deadline approaching without sign-off',
];

// ── Helpers ─────────────────────────────────────────────
const authHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
};

const updateOnboardingAPI = async (status, step, extra = {}) => {
  try {
    await fetch(`${API_URL}/api/auth/onboarding`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ status, currentStep: step, ...extra }),
    });
  } catch {}
};

// ── Progress Bar ────────────────────────────────────────
const ProgressBar = ({ current, total }) => (
  <div className="flex items-center gap-2 mb-6" data-testid="onboarding-progress">
    {Array.from({ length: total }, (_, i) => (
      <div key={i} className="flex items-center gap-2 flex-1">
        <div className={`h-1.5 rounded-full flex-1 transition-all duration-500 ${i < current ? 'bg-gradient-to-r from-indigo-500 to-cyan-500' : i === current ? 'bg-indigo-500/40' : 'bg-white/10'}`} />
      </div>
    ))}
    <span className="text-xs text-gray-500 shrink-0 ml-1">Step {current + 1} of {total}</span>
  </div>
);

// ── Step 1: Welcome + Personalization ───────────────────
const WelcomeStep = ({ onContinue, onSkip }) => {
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');

  return (
    <div className="text-center" data-testid="onboarding-step-welcome">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-5 border border-indigo-500/20">
        <Building2 className="w-7 h-7 text-indigo-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Welcome to D3 Command</h2>
      <p className="text-gray-400 text-sm max-w-sm mx-auto mb-8">Let's set up your workspace in under 2 minutes.</p>

      <div className="space-y-4 max-w-xs mx-auto text-left">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Company Name <span className="text-gray-600">(optional)</span></label>
          <input type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder="Acme Corp"
            className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-indigo-500/40 transition-colors"
            data-testid="onboarding-company-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">Your Role</label>
          <select value={role} onChange={e => setRole(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-indigo-500/40 transition-colors appearance-none"
            data-testid="onboarding-role-select">
            <option value="" className="bg-[#0d1221]">Select your role...</option>
            {ROLES.map(r => <option key={r.value} value={r.value} className="bg-[#0d1221]">{r.label}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-8 space-y-3 max-w-xs mx-auto">
        <button onClick={() => onContinue({ company, role })}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white font-semibold hover:opacity-90 transition-opacity"
          data-testid="onboarding-welcome-continue-btn">
          Continue <ArrowRight className="w-4 h-4" />
        </button>
        <button onClick={onSkip} className="text-xs text-gray-500 hover:text-gray-300 transition-colors" data-testid="onboarding-skip-btn">
          Skip onboarding
        </button>
      </div>
    </div>
  );
};

// ── Step 2: Create First Task ───────────────────────────
const CreateTaskStep = ({ onComplete, onSkip }) => {
  const { createTask } = useData();
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [done, setDone] = useState(false);
  const suggestion = SAMPLE_TASKS[Math.floor(Math.random() * SAMPLE_TASKS.length)];

  const handleCreate = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      await createTask({ title: title.trim(), priority: 'medium', column: 'todo' });
      setDone(true);
      setTimeout(() => onComplete(), 1200);
    } catch { setCreating(false); }
  };

  return (
    <div className="text-center" data-testid="onboarding-step-task">
      {done ? (
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="py-8">
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white">Task created!</h3>
          <p className="text-gray-400 text-sm mt-1">Great start — moving on...</p>
        </motion.div>
      ) : (
        <>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mx-auto mb-5 border border-purple-500/20">
            <Plus className="w-7 h-7 text-purple-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Create your first task</h2>
          <p className="text-gray-400 text-sm max-w-sm mx-auto mb-6">Start tracking execution — enter a task your team needs to get done.</p>

          <div className="max-w-sm mx-auto space-y-3">
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder={suggestion}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-purple-500/40 transition-colors"
              data-testid="onboarding-task-input" autoFocus />
            <button onClick={() => setTitle(suggestion)} className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors" data-testid="onboarding-task-suggestion-btn">
              Use suggestion: "{suggestion}"
            </button>
            <button onClick={handleCreate} disabled={!title.trim() || creating}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid="onboarding-create-task-btn">
              {creating ? 'Creating...' : 'Create Task'} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <button onClick={onSkip} className="text-xs text-gray-500 hover:text-gray-300 transition-colors mt-4" data-testid="onboarding-skip-btn">
            Skip this step
          </button>
        </>
      )}
    </div>
  );
};

// ── Step 3: Log First Risk ──────────────────────────────
const LogRiskStep = ({ onComplete, onSkip }) => {
  const { createRisk } = useData();
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [done, setDone] = useState(false);
  const suggestion = SAMPLE_RISKS[Math.floor(Math.random() * SAMPLE_RISKS.length)];

  const handleCreate = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      await createRisk({ title: title.trim(), severity: 'medium', status: 'open' });
      setDone(true);
      setTimeout(() => onComplete(), 1200);
    } catch { setCreating(false); }
  };

  return (
    <div className="text-center" data-testid="onboarding-step-risk">
      {done ? (
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="py-8">
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white">Risk logged!</h3>
          <p className="text-gray-400 text-sm mt-1">You're building visibility already...</p>
        </motion.div>
      ) : (
        <>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center mx-auto mb-5 border border-orange-500/20">
            <AlertTriangle className="w-7 h-7 text-orange-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Log your first risk</h2>
          <p className="text-gray-400 text-sm max-w-sm mx-auto mb-6">Capture a risk to see how D3 Command helps you stay ahead.</p>

          <div className="max-w-sm mx-auto space-y-3">
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder={suggestion}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-orange-500/40 transition-colors"
              data-testid="onboarding-risk-input" autoFocus />
            <button onClick={() => setTitle(suggestion)} className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors" data-testid="onboarding-risk-suggestion-btn">
              Use suggestion: "{suggestion}"
            </button>
            <button onClick={handleCreate} disabled={!title.trim() || creating}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid="onboarding-create-risk-btn">
              {creating ? 'Logging...' : 'Log Risk'} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <button onClick={onSkip} className="text-xs text-gray-500 hover:text-gray-300 transition-colors mt-4" data-testid="onboarding-skip-btn">
            Skip this step
          </button>
        </>
      )}
    </div>
  );
};

// ── Step 4: AI Insight + Completion ─────────────────────
const InsightStep = ({ onComplete }) => (
  <div className="text-center" data-testid="onboarding-step-insight">
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center mx-auto mb-5 border border-cyan-500/20">
      <Sparkles className="w-7 h-7 text-cyan-400" />
    </div>
    <h2 className="text-xl font-bold text-white mb-2">AI is working for you</h2>
    <p className="text-gray-400 text-sm max-w-sm mx-auto mb-6">
      D3 Command's AI continuously analyzes tasks, risks, and meetings — surfacing insights so you can focus on what matters.
    </p>

    <div className="max-w-sm mx-auto space-y-3 mb-6">
      {[
        { icon: BarChart3, text: 'Real-time dashboards track every task and risk', color: 'text-indigo-400' },
        { icon: Sparkles, text: 'AI flags declining productivity and emerging blockers', color: 'text-cyan-400' },
        { icon: Users, text: 'Team accountability across every project', color: 'text-purple-400' },
      ].map((item, i) => (
        <motion.div key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.15 }}
          className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5 text-left">
          <item.icon className={`w-4 h-4 ${item.color} shrink-0`} />
          <span className="text-sm text-gray-300">{item.text}</span>
        </motion.div>
      ))}
    </div>

    <button onClick={onComplete}
      className="w-full max-w-sm mx-auto flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white font-semibold hover:opacity-90 transition-opacity"
      data-testid="onboarding-finish-btn">
      Complete Setup <ChevronRight className="w-4 h-4" />
    </button>
  </div>
);

// ── Completion Modal ────────────────────────────────────
const CompletionModal = ({ onDashboard, onInvite, onDismiss }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="onboarding-complete-modal">
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onDismiss} />
    <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="relative w-[460px] rounded-3xl bg-[#0d1221] border border-emerald-500/20 shadow-2xl shadow-emerald-500/10 overflow-hidden"
    >
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-emerald-500 via-cyan-500 to-emerald-500" />

      {/* Confetti-like particles */}
      <div className="absolute top-4 left-8 w-2 h-2 rounded-full bg-indigo-400/40 animate-pulse" />
      <div className="absolute top-12 right-12 w-1.5 h-1.5 rounded-full bg-cyan-400/40 animate-pulse" style={{ animationDelay: '0.3s' }} />
      <div className="absolute top-8 right-24 w-2 h-2 rounded-full bg-emerald-400/40 animate-pulse" style={{ animationDelay: '0.6s' }} />

      <div className="relative p-8 text-center">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.2 }}
          className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-5 border border-emerald-500/20">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
        </motion.div>
        <h2 className="text-2xl font-bold text-white mb-2" data-testid="onboarding-complete-title">You're all set!</h2>
        <p className="text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">
          You now have real-time visibility into tasks, risks, and performance.
        </p>

        <div className="mt-8 space-y-3">
          <button onClick={onDashboard}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white font-semibold hover:opacity-90 transition-opacity"
            data-testid="onboarding-go-dashboard-btn">
            Go to Dashboard <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={onInvite}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white/5 text-white text-sm font-medium hover:bg-white/10 transition-colors border border-white/10"
            data-testid="onboarding-invite-team-btn">
            <Users className="w-4 h-4" /> Invite Team Members
          </button>
          <button onClick={onDismiss} className="text-xs text-gray-500 hover:text-gray-300 transition-colors" data-testid="onboarding-dismiss-btn">
            Book a Demo
          </button>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

// ── Main OnboardingFlow ─────────────────────────────────
export const OnboardingFlow = ({ onNavigate }) => {
  const { user, isDemo } = useAuth();
  const [step, setStep] = useState(0);
  const [showFlow, setShowFlow] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const totalSteps = 4;

  // Determine if onboarding should trigger
  useEffect(() => {
    if (!user) return;
    // Don't show for demo users (they get ProductTour instead)
    if (isDemo) return;
    // Don't show for client users (they get ClientOnboardingScreen instead)
    if (user.isClient) return;
    // Check backend flag
    const status = user.onboardingStatus || 'not_started';
    if (status === 'completed' || status === 'skipped') return;
    // Check localStorage fallback
    if (localStorage.getItem(OB_KEY) === 'completed' || localStorage.getItem(OB_KEY) === 'skipped') return;
    // Resume from last step
    const savedStep = user.onboardingStep || 0;
    setStep(savedStep);
    setShowFlow(true);
    // Mark as started
    if (status === 'not_started') {
      updateOnboardingAPI('in_progress', savedStep);
    }
  }, [user, isDemo]);

  const advanceStep = useCallback((nextStep) => {
    setStep(nextStep);
    updateOnboardingAPI('in_progress', nextStep);
  }, []);

  const skipOnboarding = useCallback(() => {
    setShowFlow(false);
    updateOnboardingAPI('skipped', step);
    localStorage.setItem(OB_KEY, 'skipped');
  }, [step]);

  const completeOnboarding = useCallback(() => {
    setShowFlow(false);
    setShowComplete(true);
    updateOnboardingAPI('completed', totalSteps);
    localStorage.setItem(OB_KEY, 'completed');
  }, []);

  const handleWelcomeContinue = useCallback(({ company, role }) => {
    updateOnboardingAPI('in_progress', 1, { company: company || undefined, role: role || undefined });
    advanceStep(1);
  }, [advanceStep]);

  if (!showFlow && !showComplete) return null;

  return (
    <>
      {/* Onboarding Overlay */}
      <AnimatePresence>
        {showFlow && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="onboarding-overlay">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div
              key={step}
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -16 }}
              transition={{ duration: 0.25 }}
              className="relative w-[500px] rounded-3xl bg-[#0d1221] border border-white/10 shadow-2xl overflow-hidden"
            >
              {/* Top accent bar */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-indigo-500 via-cyan-500 to-indigo-500" />

              <div className="p-8">
                <ProgressBar current={step} total={totalSteps} />

                {step === 0 && <WelcomeStep onContinue={handleWelcomeContinue} onSkip={skipOnboarding} />}
                {step === 1 && <CreateTaskStep onComplete={() => advanceStep(2)} onSkip={() => advanceStep(2)} />}
                {step === 2 && <LogRiskStep onComplete={() => advanceStep(3)} onSkip={() => advanceStep(3)} />}
                {step === 3 && <InsightStep onComplete={completeOnboarding} />}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Completion Modal */}
      <AnimatePresence>
        {showComplete && (
          <CompletionModal
            onDashboard={() => { setShowComplete(false); if (onNavigate) onNavigate('dashboard'); }}
            onInvite={() => { setShowComplete(false); if (onNavigate) onNavigate('settings'); }}
            onDismiss={() => setShowComplete(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default OnboardingFlow;
